import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import api from "services/api";
import { TIALAI_UPLOADS } from "utils";
import { getCurrentUser, requireAuth } from "actions/AuthAction";

const statusLabel = {
  DRAFT: "Draft",
  DIAJUKAN: "Diajukan",
  VERIFIKASI: "Diverifikasi",
  PERBAIKAN: "Perlu Perbaikan",
  PROSES_TEKNIS: "Proses Teknis",
  DISETUJUI: "Disetujui",
  DITOLAK: "Ditolak",
  PENANDATANGANAN: "Penandatanganan",
  SELESAI: "Selesai",
};

const parseDataTambahan = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

function DetailPengajuan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const izinFileRef = useRef(null);
  const [syaratId, setSyaratId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [syaratList, setSyaratList] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [dokumenIzin, setDokumenIzin] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const user = getCurrentUser();
  const isPemohon = user?.role === "PEMOHON";
  const isStaff = ["PETUGAS", "VERIFIKATOR", "PIMPINAN", "ADMIN", "SUPER_ADMIN"].includes(user?.role);
  const canTerbitkan = ["PIMPINAN", "ADMIN", "PETUGAS", "SUPER_ADMIN"].includes(user?.role);

  const loadData = () => {
    setLoading(true);
    api
      .get(`pengajuan/${id}`)
      .then(async (res) => {
        const pengajuan = res.data?.data;
        setData(pengajuan);
        setFieldValues(parseDataTambahan(pengajuan?.data_tambahan));

        if (pengajuan?.jenis_izin_id) {
          const syaratRes = await api.get(`jenis-izin/${pengajuan.jenis_izin_id}/syarat`);
          setSyaratList(syaratRes.data?.data || []);
        }

        if (pengajuan?.status === "SELESAI") {
          try {
            const izinRes = await api.get(`pengajuan/${id}/izin`);
            setDokumenIzin(izinRes.data?.data);
          } catch {
            setDokumenIzin(null);
          }
        } else {
          setDokumenIzin(null);
        }
      })
      .catch(() => alert("Gagal memuat detail pengajuan"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    requireAuth(navigate);
    loadData();
  }, [id, navigate]);

  const canEdit = isPemohon && data && ["DRAFT", "PERBAIKAN"].includes(data.status);

  const handleUploadClick = (sid) => {
    setSyaratId(sid);
    fileRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !syaratId) return;

    const form = new FormData();
    form.append("file", file);
    form.append("syarat_izin_id", syaratId);

    setUploading(true);
    try {
      await api.post(`pengajuan/${id}/dokumen`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Upload gagal");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const updateField = (syaratItem, value) => {
    setFieldValues((prev) => ({ ...prev, [`syarat_${syaratItem.id}`]: value }));
  };

  const handleSaveFields = async () => {
    setSavingFields(true);
    try {
      await api.put(`pengajuan/${id}`, { data_tambahan: fieldValues });
      alert("Data persyaratan berhasil disimpan");
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Gagal menyimpan data");
    } finally {
      setSavingFields(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (canEdit) await api.put(`pengajuan/${id}`, { data_tambahan: fieldValues });
      await api.post(`pengajuan/${id}/submit`);
      alert("Pengajuan berhasil dikirim");
      loadData();
    } catch (err) {
      const missing = err.response?.data?.missing;
      if (missing?.length) {
        alert(`Persyaratan belum lengkap:\n- ${missing.join("\n- ")}`);
      } else {
        alert(err.response?.data?.message || "Submit gagal");
      }
    }
  };

  const handleStaffAction = async (action, body = {}) => {
    try {
      await api.post(`verifikasi/pengajuan/${id}/${action}`, body);
      alert("Status berhasil diperbarui");
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Aksi gagal");
    }
  };

  const handleTerbitkanIzin = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const pejabat = prompt("Nama pejabat penandatangan:") || "";
    const form = new FormData();
    form.append("file", file);
    form.append("ditandatangani_oleh", pejabat);
    try {
      await api.post(`pengajuan/${id}/terbitkan-izin`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Dokumen izin berhasil diterbitkan");
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Gagal menerbitkan izin");
    } finally {
      e.target.value = "";
    }
  };

  const nonFileSyarat = syaratList.filter((s) => s.jenis_input !== "FILE");

  if (loading || !data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>Memuat...</MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <input type="file" ref={fileRef} hidden onChange={handleFileChange} />
      <input type="file" ref={izinFileRef} hidden accept=".pdf,.jpg,.jpeg,.png" onChange={handleTerbitkanIzin} />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ mb: 3 }}>
              <MDBox p={3}>
                <MDBox display="flex" justifyContent="space-between" mb={2}>
                  <MDBox>
                    <MDTypography variant="h5">{data.nomor_pengajuan}</MDTypography>
                    <MDTypography variant="button" color="text">
                      {data.nama_jenis_izin}
                    </MDTypography>
                  </MDBox>
                  <Chip label={statusLabel[data.status] || data.status} color="info" />
                </MDBox>
                <MDTypography variant="caption" color="text" display="block">
                  Pemohon: {data.nama_pemohon}
                </MDTypography>
              </MDBox>
            </Card>

            {nonFileSyarat.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <MDBox p={3}>
                  <MDTypography variant="h6" mb={2}>
                    Data Persyaratan (Formulir)
                  </MDTypography>
                  {nonFileSyarat.map((s) => {
                    const key = `syarat_${s.id}`;
                    const val = fieldValues[key];

                    if (s.jenis_input === "COORDINATE") {
                      const coord = val || {};
                      return (
                        <MDBox key={s.id} mb={2}>
                          <MDTypography variant="button" fontWeight="medium" mb={1} display="block">
                            {s.nama_syarat} {s.wajib ? "*" : ""}
                          </MDTypography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <MDInput
                                label="Latitude"
                                fullWidth
                                disabled={!canEdit}
                                value={coord.latitude || coord.lat || ""}
                                onChange={(e) =>
                                  updateField(s, {
                                    ...coord,
                                    latitude: e.target.value,
                                  })
                                }
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <MDInput
                                label="Longitude"
                                fullWidth
                                disabled={!canEdit}
                                value={coord.longitude || coord.lng || ""}
                                onChange={(e) =>
                                  updateField(s, {
                                    ...coord,
                                    longitude: e.target.value,
                                  })
                                }
                              />
                            </Grid>
                          </Grid>
                        </MDBox>
                      );
                    }

                    return (
                      <MDBox key={s.id} mb={2}>
                        <MDInput
                          label={`${s.nama_syarat}${s.wajib ? " *" : ""}`}
                          fullWidth
                          type={s.jenis_input === "NUMBER" ? "number" : "text"}
                          disabled={!canEdit}
                          value={val ?? ""}
                          onChange={(e) => updateField(s, e.target.value)}
                        />
                      </MDBox>
                    );
                  })}
                  {canEdit && (
                    <MDButton color="info" onClick={handleSaveFields} disabled={savingFields}>
                      {savingFields ? "Menyimpan..." : "Simpan Data Formulir"}
                    </MDButton>
                  )}
                </MDBox>
              </Card>
            )}

            <Card sx={{ mb: 3 }}>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={2}>
                  Dokumen Persyaratan
                </MDTypography>
                <List>
                  {syaratList
                    .filter((s) => s.jenis_input === "FILE")
                    .map((s, idx, arr) => {
                      const dokumen = (data.dokumen || []).find(
                        (d) => d.syarat_izin_id === s.id
                      );
                      return (
                        <ListItem key={s.id} divider={idx < arr.length - 1}>
                          <ListItemText
                            primary={`${s.urutan}. ${s.nama_syarat}`}
                            secondary={
                              dokumen
                                ? `Status: ${dokumen.status_verifikasi}${dokumen.catatan_verifikasi ? ` — ${dokumen.catatan_verifikasi}` : ""}`
                                : s.wajib
                                  ? "Belum diupload (wajib)"
                                  : "Belum diupload (opsional)"
                            }
                          />
                          <MDBox display="flex" gap={1}>
                            {dokumen?.path_file && (
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="info"
                                href={`${TIALAI_UPLOADS}/${dokumen.path_file.replace(/\\/g, "/")}`}
                                target="_blank"
                              >
                                Lihat
                              </MDButton>
                            )}
                            {canEdit && (
                              <MDButton
                                size="small"
                                color="info"
                                onClick={() => handleUploadClick(s.id)}
                                disabled={uploading}
                              >
                                {dokumen ? "Ganti" : "Upload"}
                              </MDButton>
                            )}
                          </MDBox>
                        </ListItem>
                      );
                    })}
                </List>
              </MDBox>
            </Card>

            {dokumenIzin && (
              <Card sx={{ mb: 3 }}>
                <MDBox p={3}>
                  <MDTypography variant="h6" mb={1}>
                    Dokumen Izin
                  </MDTypography>
                  <MDTypography variant="button" display="block" mb={1}>
                    Nomor: {dokumenIzin.nomor_izin}
                  </MDTypography>
                  <MDTypography variant="button" display="block" mb={2}>
                    Kode verifikasi: {dokumenIzin.kode_verifikasi}
                  </MDTypography>
                  <MDButton
                    variant="gradient"
                    color="success"
                    href={`${TIALAI_UPLOADS}/${dokumenIzin.file_izin.replace(/\\/g, "/")}`}
                    target="_blank"
                  >
                    Download Dokumen Izin
                  </MDButton>
                </MDBox>
              </Card>
            )}

            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={2}>
                  Riwayat Proses
                </MDTypography>
                <List dense>
                  {(data.riwayat || []).map((r) => (
                    <ListItem key={r.id}>
                      <ListItemText
                        primary={`${statusLabel[r.status] || r.status} — ${r.keterangan || "-"}`}
                        secondary={new Date(r.created_at).toLocaleString("id-ID")}
                      />
                    </ListItem>
                  ))}
                </List>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6" mb={2}>
                  Aksi
                </MDTypography>

                {canEdit && (
                  <>
                    <MDButton variant="gradient" color="success" fullWidth onClick={handleSubmit} sx={{ mb: 1 }}>
                      Submit Pengajuan
                    </MDButton>
                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                {isStaff && data.status === "DIAJUKAN" && (
                  <MDButton fullWidth color="info" sx={{ mb: 1 }} onClick={() => handleStaffAction("verifikasi")}>
                    Mulai Verifikasi
                  </MDButton>
                )}
                {isStaff && ["VERIFIKASI", "DIAJUKAN"].includes(data.status) && (
                  <>
                    <MDButton fullWidth color="success" sx={{ mb: 1 }} onClick={() => handleStaffAction("setujui")}>
                      Setujui
                    </MDButton>
                    <MDButton
                      fullWidth
                      color="warning"
                      sx={{ mb: 1 }}
                      onClick={() => {
                        const keterangan = prompt("Catatan perbaikan:");
                        if (keterangan) handleStaffAction("perbaikan", { keterangan });
                      }}
                    >
                      Minta Perbaikan
                    </MDButton>
                    <MDButton
                      fullWidth
                      color="error"
                      sx={{ mb: 1 }}
                      onClick={() => {
                        const keterangan = prompt("Alasan penolakan:");
                        if (keterangan) handleStaffAction("tolak", { keterangan });
                      }}
                    >
                      Tolak
                    </MDButton>
                  </>
                )}

                {canTerbitkan && ["DISETUJUI", "PENANDATANGANAN"].includes(data.status) && (
                  <MDButton
                    fullWidth
                    color="success"
                    variant="gradient"
                    sx={{ mb: 1 }}
                    onClick={() => izinFileRef.current?.click()}
                  >
                    Terbitkan Dokumen Izin
                  </MDButton>
                )}

                <MDButton variant="text" color="dark" fullWidth onClick={() => navigate(-1)}>
                  Kembali
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DetailPengajuan;
